import type {ReactElement} from 'react';
import React, { useEffect, useState} from 'react';
import styles from '@Components/social-media/components/preview-video-player/preview-video-player.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';

interface PreviewVideoPlayerProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isReel?: boolean;
  showSoundControls?: boolean;
}

export function PreviewVideoPlayer({videoRef, isReel = false, showSoundControls = true}: PreviewVideoPlayerProps): ReactElement<any> {
  const [currentTime, setCurrentTime] = useState<number | undefined>(0);
  const [duration, setDuration] = useState<number | undefined>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSoundOn, setIsSoundOn] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('timeupdate', () => {
        if (videoRef.current?.currentTime) {
          setCurrentTime(videoRef.current?.currentTime);
        }
      });

      videoRef.current.addEventListener('loadedmetadata', () => {
        setDuration(videoRef.current?.duration);
      });
    }
  });

  const formatTime = (timeInSeconds: number, isCurrentTime: boolean): string => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    if (isCurrentTime && timeInSeconds === 0) {
      if (hours === 0) {
        return `0:00`;
      }

      return `0:00:00`;
    }

    if (hours > 0) {
      return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    if (minutes > 0) {
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    return `0:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const getProgressBarWidth = (): string => {
    return `${currentTime !== undefined && duration !== undefined ? ((duration - currentTime) / duration) * 100 : 0}%`;
  };

  const getElapsedProgressBarWidth = (): string => {
    return `${currentTime !== undefined && duration !== undefined ? (currentTime / duration) * 100 : 0}%`;
  };

  const isProgressBarRounded = (isElapsed: boolean): boolean => {
    let barWidth = '';

    if (isElapsed) {
      barWidth = getElapsedProgressBarWidth();
    } else {
      barWidth = getProgressBarWidth();
    }

    return barWidth === '100%';
  };

  const elapsedStyles = {
    width: `${getElapsedProgressBarWidth()}`,
    borderTopRightRadius: `${isProgressBarRounded(true) ? '10px' : '0'}`,
    borderBottomRightRadius: `${isProgressBarRounded(true) ? '10px' : '0'}`,
  };

  const remainingStyles = {
    width: `${getProgressBarWidth()}`,
    borderTopLeftRadius: `${isProgressBarRounded(false) ? '10px' : '0'}`,
    borderBottomLeftRadius: `${isProgressBarRounded(false) ? '10px' : '0'}`,
  };

  const onPlayPause = (): void => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(!isPlaying);
        return;
      }

      void videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const onChangeSound = (): void => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsSoundOn(videoRef.current.muted);
    }
  };

  const renderVideoProgressBar = (): ReactElement<any> => {
    return (
      <div className={`${styles.videoProgressBar} ${isReel ? styles.reelProgressBar : null}`}>
        <div style={elapsedStyles} className={styles.leftDiv} />
        <div style={remainingStyles} className={styles.rightDiv} />
      </div>
    );
  };

  const getFormattedCurrentTime = (): string => {
    if (currentTime) {
      return formatTime(currentTime, true);
    }

    return '';
  };

  const getFormattedTotalTime = (): string => {
    if (duration) {
      return formatTime(duration, false);
    }

    return '';
  };

  const renderVideoTime = (): ReactElement<any> => {
    return (
      <div className={`${styles.playerTime} content-body-white body-xxs ${isReel ? styles.reelPlayerTime : ''}`}>
        {getFormattedCurrentTime()} / {getFormattedTotalTime()}
      </div>
    );
  };

  const renderVideoControlsMini = (): ReactElement<any> => {
    return (
      <Icon
        className={`${styles.playPauseIcon} ${isReel ? styles.reelPlayPause : ''}`}
        icon={`${isPlaying ? 'icon-pause' : 'icon-play-video'}`}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        type={IconType.DARK_GHOST}
        onClick={onPlayPause}
      />
    );
  };

  const renderVideoControlsLarge = (): ReactElement<any> => {
    return (
      <Icon
        className={styles.playPauseLargeIcon}
        icon={`${isPlaying ? 'icon-pause' : 'icon-play-video'}`}
        size={IconSize.SIZE_ICON_24}
        shape={IconShape.CIRCLE}
        type={IconType.SHADOW}
        onClick={onPlayPause}
      />
    );
  };

  const renderSoundControls = (): ReactElement<any> | null => {
    if (!showSoundControls) {
      return null;
    }

    return (
      <Icon
        onClick={onChangeSound}
        className={`${styles.volumeControl} ${isReel ? styles.reelSoundControl : ''}`}
        size={IconSize.SIZE_ICON_16}
        icon={`${isSoundOn ? 'icon-sound' : 'icon-sound-off'}`}
        type={IconType.DARK_GHOST}
        shape={IconShape.CIRCLE}
      />
    );
  };

  return (
    <>
      {renderVideoProgressBar()}
      {renderVideoTime()}
      {renderVideoControlsMini()}
      {renderVideoControlsLarge()}
      {renderSoundControls()}
      <div className={styles.overlay} />
    </>
  );
}

export default PreviewVideoPlayer;
